import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Container, Form, Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Loader } from '@googlemaps/js-api-loader';
import { recaptchaSize } from '../../helpers/common';
import { submitFormData } from '../../helpers/form-api';
import { inBuildEnv } from '../../helpers/inBuildEnv';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';

const SummerSaverForm = () => {
  const initialFormData = {
    first_name: '',
    last_name: '',
    property_address: '',
    mobile_number: '',
    email: '',
    nmi: '',
    name_in_bill: '',
    accepted_terms: false,
  };

  const initialFormErrors = {
    property_address: false,
    mobile_number: false,
    nmi: false,
    accepted_terms: false,
    email: false,
  };

  const recaptchaRef = useRef(null);
  const addressRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState(initialFormErrors);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [googleApiLoaded, setGoogleApiLoaded] = useState(false);

  useEffect(() => {
    if (
      !formErrors.mobile_number &&
      !formErrors.nmi &&
      !formErrors.accepted_terms &&
      !formErrors.property_address &&
      !formErrors.email &&
      captcha
    ) {
      setError(false);
    }
  }, [formErrors, setError, captcha]);

  const handleCheckbox = (name) => {
    const oldValue = formData[name];
    const newValue = !oldValue;
    validateForm(name, newValue);
    setFormData({
      ...formData,
      [name]: newValue,
    });
  };

  const handleFocus = (event) => {
    event.target.setAttribute('autocomplete', 'disabled');
  };

  const handleInputChange = (event) => {
    const target = event.currentTarget;
    const value = target.value;
    const name = target.name;
    const phoneRegex = /[^0-9]/g;
    const nmiRegex = /[^A-Za-z0-9]/g;

    let formattedValue;
    switch (name) {
      case 'mobile_number':
        formattedValue = value.replaceAll(phoneRegex, '');
        break;
      case 'nmi':
        formattedValue = value.replaceAll(nmiRegex, '');
        break;
      default:
        formattedValue = value;
        break;
    }

    validateForm(name, formattedValue);
    setFormData({
      ...formData,
      [name]: formattedValue,
    });
  };

  const handleCaptcha = (value) => {
    setCaptcha(value);
  };

  const validateForm = (fieldName, value) => {
    let error = false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    switch (fieldName) {
      case 'mobile_number':
        if (!value.startsWith('04')) {
          error = 'Must start with 04';
        } else if (value.length !== 10) {
          error = 'Must be 10 characters in length';
        }
        break;
      case 'nmi':
        if (value.length !== 11) {
          error = 'Must be 11 characters in length';
        }
        break;
      case 'email':
        if (!emailRegex.test(value)) {
          error = 'Email is not valid';
        }
        break;
      case 'accepted_terms':
        if (value === false || value === 'false') {
          error = 'Must accept terms and conditions';
        }
        break;
      case 'property_address':
        if (!value) {
          error = 'Must select a valid address';
        }
        break;
    }

    setFormErrors({
      ...formErrors,
      [fieldName]: error,
    });

    return error;
  };

  const handleSubmit = async () => {
    let hasErrors = false;
    Object.keys(formErrors).every((field) => {
      const result = validateForm(field, formData[field]);
      if (result !== false) {
        hasErrors = true;
        return false; // Error has been found
      }
      return true; // No error on this field
    });

    if (hasErrors) {
      setError('Must resolve all form errors before submitting');
      return;
    }

    try {
      setError(false);
      setSuccess(false);
      setSubmitting(true);

      if (captcha) {
        const dataToSubmit = {
          ...formData,
          captchaResponse: captcha,
        };

        const response = await submitFormData('summer_saver', dataToSubmit);

        if (response.status !== 200) {
          setSuccess(false);
          setError(response.error);
          setSubmitting(false);
          setCaptcha(null);
        } else {
          setSuccess('Success');
          setError(false);
          setSubmitting(false);
          setCaptcha(null);
          setFormData(initialFormData);

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'summer_saver_form_submission',
          });
        }

        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      } else {
        setError('Please tick ReCAPTCHA');
        setSuccess(false);
        setSubmitting(false);
      }
    } catch {
      setError('Error');
      setSuccess(false);
      setSubmitting(false);
    }
  };

  const submitText = () => {
    if (submitting) {
      return 'Submitting...';
    }
    return 'Submit';
  };

  useEffect(() => {
    if (!googleApiLoaded && !inBuildEnv()) {
      const loader = new Loader({
        apiKey: process.env.GATSBY_GOOGLE_MAPS_KEY || '',
        version: 'weekly',
      });

      loader.importLibrary('places').then((places) => {
        addressRef.current = new places.Autocomplete(
          document.getElementById('form-property-address'),
          { componentRestrictions: { country: 'AU' } }
        );

        setGoogleApiLoaded(true);
      });
    }
  }, [googleApiLoaded, addressRef]);

  /**
   * Note: this block of code needs to run in this location,
   * otherwise the `place_changed` event won't fire on initial load
   */
  if (addressRef && addressRef.current) {
    addressRef.current.addListener('place_changed', () => {
      const place = addressRef.current.getPlace();
      setFormData({
        ...formData,
        property_address: place.formatted_address,
      });
      setFormErrors({
        ...formErrors,
        property_address: false,
      });
    });
  }

  return (
    <section className="summer-saver-form">
      <Container>
        <div className="grid-wrapper">
          <div className="form-container">
            <Form onSubmit={handleSubmit} autoComplete="chrome-off">
              <Form.Input
                label="First name"
                required
                name="first_name"
                type="text"
                value={formData.first_name}
                onChange={handleInputChange}
                id="form-first-name"
              />
              <Form.Input
                label="Last name"
                required
                name="last_name"
                type="text"
                value={formData.last_name}
                onChange={handleInputChange}
                id="form-last-name"
              />
              <Form.Group className="stacked">
                <Form.Input
                  label="Property address"
                  onFocus={handleFocus}
                  name="property_address"
                  type="text"
                  id="form-property-address"
                  error={formErrors.property_address}
                />
                <div className="field-follow">
                  {formData.property_address ? (
                    <div className="summer-saver-form__address-wrapper">
                      <span>
                        <strong>Address:</strong> {formData.property_address}
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setFormData({
                            ...formData,
                            property_address: '',
                          });
                          document.getElementById(
                            'form-property-address'
                          ).value = '';
                        }}
                        className="summer-saver-form__address-clear"
                      >
                        Clear
                      </button>
                    </div>
                  ) : null}
                </div>
              </Form.Group>
              <Form.Input
                label="Mobile number"
                required
                name="mobile_number"
                type="text"
                value={formData.mobile_number}
                onChange={handleInputChange}
                id="form-mobile-number"
                error={
                  formErrors.mobile_number
                    ? {
                        content: formErrors.mobile_number,
                      }
                    : false
                }
              />
              <Form.Input
                label="Email"
                required
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                id="form-email"
                error={
                  formErrors.email
                    ? {
                        content: formErrors.email,
                      }
                    : false
                }
              />
              <Form.Input
                label="NMI number as shown in electricity bill"
                required
                name="nmi"
                type="text"
                value={formData.nmi}
                onChange={handleInputChange}
                id="form-nmi"
                error={formErrors.nmi}
              />
              <Form.Input
                label="Name as shown in electricity bill"
                required
                name="name_in_bill"
                type="text"
                value={formData.name_in_bill}
                onChange={handleInputChange}
                id="form-name-in-bill"
              />
              <Form.Checkbox
                label={{
                  children: (
                    <span>
                      I have read and accepted the{' '}
                      <a
                        href="/summer-saver/terms-and-conditions/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms and Conditions
                      </a>
                    </span>
                  ),
                }}
                name="accepted_terms"
                checked={formData.accepted_terms}
                onChange={() => handleCheckbox('accepted_terms')}
                error={
                  formErrors.accepted_terms
                    ? { content: formErrors.accepted_terms, pointing: 'left' }
                    : false
                }
              />
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={recaptchaKey}
                onChange={handleCaptcha}
                size={recaptchaSize()}
              />
              <Button
                className="form--submit-btn"
                disabled={submitting}
                loading={submitting}
              >
                {submitText()}
              </Button>
              {success ? (
                <div className="success-message">{success}</div>
              ) : null}
              {error ? <div className="error-message">{error}</div> : null}
            </Form>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default SummerSaverForm;
